import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { track } from "@/lib/analytics";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null=loading, false=guest, obj=user
    const [ready, setReady] = useState(false);

    const refresh = useCallback(async () => {
        // CRITICAL: skip /me check if session_id is in URL hash (OAuth callback in flight)
        if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
            setReady(true);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            setUser(false);
        } finally {
            setReady(true);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const login = async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            if (data.token) localStorage.setItem("pawlink_token", data.token);
            setUser({ id: data.id, email: data.email, name: data.name, plan: data.plan, language: data.language });
            track("login", { method: "email" });
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    };

    const register = async (email, password, name, language) => {
        try {
            const { data } = await api.post("/auth/register", { email, password, name, language });
            if (data.token) localStorage.setItem("pawlink_token", data.token);
            setUser({ id: data.id, email: data.email, name: data.name, plan: data.plan, language: data.language });
            track("sign_up", { method: "email" });
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch {}
        localStorage.removeItem("pawlink_token");
        setUser(false);
    };

    // Called by ResetPassword after a successful /auth/reset-password — the
    // backend already issued a fresh token; we just persist it and hydrate user.
    const hydrateFromReset = (data) => {
        if (data?.token) localStorage.setItem("pawlink_token", data.token);
        setUser({ id: data.id, email: data.email, name: data.name, plan: data.plan, language: data.language });
        track("login", { method: "password_reset" });
    };

    return (
        <AuthContext.Provider value={{ user, ready, login, register, logout, refresh, hydrateFromReset }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be inside AuthProvider");
    return ctx;
}
